import { useCallback, useEffect, useRef, useState } from 'react'
import { Canvas } from '@react-three/fiber'
import { OrbitControls, Grid, Environment, Center } from '@react-three/drei'
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'

// ---- Types -----------------------------------------------------------

interface ModelReport {
  fileName: string
  fileSizeBytes: number
  isRigged: boolean
  boneNames: string[]
  skinnedMeshCount: number
  meshCount: number
  triangleCount: number
  vertexCount: number
  materialCount: number
  morphTargetNames: string[]
  textureCount: number
}

// ---- GLB loading / analysis -------------------------------------------

function loadGLB(
  arrayBuffer: ArrayBuffer,
  fileName: string
): Promise<{ scene: THREE.Group; report: ModelReport }> {
  return new Promise((resolve, reject) => {
    const loader = new GLTFLoader()
    // DRACO support: some export pipelines (incl. some Blender exports) compress
    // geometry with Draco. Without this the load silently fails on those files.
    const draco = new DRACOLoader()
    draco.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.7/')
    loader.setDRACOLoader(draco)

    loader.parse(
      arrayBuffer,
      '',
      (gltf) => {
        const scene = gltf.scene
        const boneNames: string[] = []
        const morphTargetNames = new Set<string>()
        const materials = new Set<THREE.Material>()
        const textures = new Set<THREE.Texture>()
        let meshCount = 0
        let skinnedMeshCount = 0
        let triangleCount = 0
        let vertexCount = 0

        scene.traverse((obj) => {
          if ((obj as THREE.Bone).isBone) {
            boneNames.push(obj.name)
          }
          if ((obj as THREE.Mesh).isMesh) {
            meshCount++
            const mesh = obj as THREE.Mesh
            const geom = mesh.geometry
            if (geom) {
              vertexCount += geom.attributes.position?.count ?? 0
              triangleCount += geom.index
                ? geom.index.count / 3
                : (geom.attributes.position?.count ?? 0) / 3
            }
            if (geom?.morphAttributes) {
              Object.keys(geom.morphAttributes).forEach((k) => {
                if (mesh.morphTargetDictionary) {
                  Object.keys(mesh.morphTargetDictionary).forEach((name) =>
                    morphTargetNames.add(name)
                  )
                } else {
                  morphTargetNames.add(k)
                }
              })
            }
            const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
            mats.forEach((m) => {
              if (!m) return
              materials.add(m)
              ;['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'emissiveMap', 'aoMap'].forEach(
                (slot) => {
                  const tex = (m as any)[slot] as THREE.Texture | undefined
                  if (tex) textures.add(tex)
                }
              )
            })
          }
          if ((obj as THREE.SkinnedMesh).isSkinnedMesh) {
            skinnedMeshCount++
          }
        })

        const report: ModelReport = {
          fileName,
          fileSizeBytes: arrayBuffer.byteLength,
          isRigged: boneNames.length > 0 && skinnedMeshCount > 0,
          boneNames,
          skinnedMeshCount,
          meshCount,
          triangleCount: Math.round(triangleCount),
          vertexCount,
          materialCount: materials.size,
          morphTargetNames: Array.from(morphTargetNames),
          textureCount: textures.size,
        }

        resolve({ scene, report })
      },
      (err) => reject(err)
    )
  })
}

function exportGLB(scene: THREE.Object3D, fileName: string) {
  const exporter = new GLTFExporter()
  exporter.parse(
    scene,
    (result) => {
      const blob =
        result instanceof ArrayBuffer
          ? new Blob([result], { type: 'model/gltf-binary' })
          : new Blob([JSON.stringify(result)], { type: 'model/gltf+json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      const base = fileName.replace(/\.(glb|gltf)$/i, '')
      a.href = url
      a.download = `${base}-export.glb`
      a.click()
      URL.revokeObjectURL(url)
    },
    (err) => console.error('Export failed:', err),
    { binary: true }
  )
}

// ---- Viewer -------------------------------------------------------------

function Model({ scene }: { scene: THREE.Group }) {
  return (
    <Center>
      <primitive object={scene} />
    </Center>
  )
}

// ---- UI -------------------------------------------------------------

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

const LOCKED_FEATURES = [
  {
    name: 'Proportion editor',
    needs: 'a rigged skeleton',
    what: 'Sliders that scale individual bones (height, limb length, torso/hip proportions) with live preview.',
  },
  {
    name: 'Wardrobe / clothing uploader',
    needs: 'a rigged skeleton with standard bone names',
    what: 'Import garment GLBs, auto-skin them to the same skeleton by matching bone names, build a saved outfit library.',
  },
  {
    name: 'Hair changer',
    needs: 'a head bone',
    what: 'Swap hair meshes attached to the head bone, saved as presets.',
  },
  {
    name: 'Tattoos / decals',
    needs: 'nothing — works on any skin texture',
    what: 'This one does not actually require rigging. It edits the skin material\u2019s texture directly and can be added in the next pass regardless of rig status.',
  },
  {
    name: 'Animation playback',
    needs: 'a rigged skeleton with a standard (e.g. Mixamo) bone naming scheme',
    what: 'Load and blend animation clips (walk, idle, talk) driven by the skeleton.',
  },
]

export default function App() {
  const [scene, setScene] = useState<THREE.Group | null>(null)
  const [report, setReport] = useState<ModelReport | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [showBones, setShowBones] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFile = useCallback(async (file: File) => {
    setError(null)
    setLoading(true)
    try {
      if (!/\.(glb|gltf)$/i.test(file.name)) {
        throw new Error('Please choose a .glb or .gltf file.')
      }
      const buf = await file.arrayBuffer()
      const { scene, report } = await loadGLB(buf, file.name)
      setScene(scene)
      setReport(report)
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load this file. It may be corrupted or an unsupported glTF variant.')
      setScene(null)
      setReport(null)
    } finally {
      setLoading(false)
    }
  }, [])

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      const file = e.dataTransfer.files?.[0]
      if (file) handleFile(file)
    },
    [handleFile]
  )

  return (
    <div className="app">
      <header className="header">
        <div>
          <h1>Amy GLB Studio</h1>
          <p className="subtitle">Phase 1 — import, inspect, export pipeline</p>
        </div>
      </header>

      <main className="main">
        <section
          className="dropzone"
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".glb,.gltf"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) handleFile(f)
            }}
          />
          {loading ? (
            <p>Loading model…</p>
          ) : scene ? (
            <p>Loaded: {report?.fileName} — tap to load a different file</p>
          ) : (
            <p>Tap to choose a .glb file, or drag one in here.</p>
          )}
        </section>

        {error && <div className="error">{error}</div>}

        {scene && (
          <>
            <div className="viewer">
              <Canvas camera={{ position: [0, 1.4, 3], fov: 45 }}>
                <ambientLight intensity={0.6} />
                <directionalLight position={[3, 5, 2]} intensity={1.2} />
                <Environment preset="city" />
                <Grid args={[10, 10]} cellColor="#1e2230" sectionColor="#2a3040" position={[0, -0.01, 0]} />
                <Model scene={scene} />
                <OrbitControls enableDamping makeDefault />
              </Canvas>
            </div>

            {report && (
              <section className="panel">
                <h2>Model report</h2>
                <dl className="report-grid">
                  <dt>File size</dt>
                  <dd>{formatBytes(report.fileSizeBytes)}</dd>
                  <dt>Rigged</dt>
                  <dd className={report.isRigged ? 'ok' : 'warn'}>
                    {report.isRigged ? `Yes — ${report.boneNames.length} bones` : 'No — static mesh'}
                  </dd>
                  <dt>Skinned meshes</dt>
                  <dd>{report.skinnedMeshCount}</dd>
                  <dt>Total meshes</dt>
                  <dd>{report.meshCount}</dd>
                  <dt>Triangles</dt>
                  <dd>{report.triangleCount.toLocaleString()}</dd>
                  <dt>Vertices</dt>
                  <dd>{report.vertexCount.toLocaleString()}</dd>
                  <dt>Materials</dt>
                  <dd>{report.materialCount}</dd>
                  <dt>Textures</dt>
                  <dd>{report.textureCount}</dd>
                  <dt>Morph targets</dt>
                  <dd>{report.morphTargetNames.length > 0 ? report.morphTargetNames.join(', ') : 'None'}</dd>
                </dl>

                {report.isRigged && (
                  <button className="link-btn" onClick={() => setShowBones((v) => !v)}>
                    {showBones ? 'Hide bone list' : `Show bone list (${report.boneNames.length})`}
                  </button>
                )}
                {showBones && (
                  <pre className="bone-list">{report.boneNames.join('\n')}</pre>
                )}

                <button className="export-btn" onClick={() => scene && exportGLB(scene, report.fileName)}>
                  Export current model as .glb
                </button>
                <p className="hint">
                  This re-serializes exactly what's in the viewer right now, including any changes made
                  by later editing tools. It proves the round-trip works: what you upload is what you get back.
                </p>
              </section>
            )}
          </>
        )}

        <section className="panel">
          <h2>What's next (locked until rigging)</h2>
          <p className="hint">
            Amy's current file loads and exports correctly, but the proportion, wardrobe, hair, and
            animation tools all need a skeleton to attach to. Once you run her through Mixamo and load
            the rigged GLB here, this app will detect it automatically (see "Rigged" above) and these
            unlock for real — nothing here is a placeholder that pretends to work.
          </p>
          <ul className="feature-list">
            {LOCKED_FEATURES.map((f) => (
              <li key={f.name}>
                <strong>{f.name}</strong> — needs {f.needs}.<br />
                <span className="hint">{f.what}</span>
              </li>
            ))}
          </ul>
        </section>
      </main>
    </div>
  )
}
